/**
 * Utility functions for working with companies, primarily de-duplication.
 *
 * Companies are de-duplicated by their primary website rather than by name,
 * since two distinct companies can legitimately share a name but not a main
 * domain. When a website is unavailable we fall back to the company name so
 * de-duplication still applies.
 */

/**
 * Normalize a website URL down to its bare host so the same company maps to a
 * single key regardless of scheme, "www." prefix, path, query or trailing slash.
 *
 * Examples:
 *   "https://www.Example.com/"     -> "example.com"
 *   "http://example.com/about?x=1" -> "example.com"
 *   "Example.com"                  -> "example.com"
 *
 * Returns an empty string when no usable host can be derived.
 */
export function normalizeWebsite(website?: string | null): string {
  let value = (website ?? '').trim().toLowerCase();
  if (!value) return '';
  // Drop the scheme (http/https/...) if present.
  value = value.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  // Keep only the host portion, dropping any path/query/fragment.
  value = value.split(/[/?#]/)[0];
  // Drop a leading "www." and any port suffix.
  value = value.replace(/^www\./, '');
  value = value.split(':')[0];
  return value.trim();
}

/**
 * Build the de-duplication key for a company: its normalized website when
 * available, otherwise its normalized (lowercased, trimmed) name.
 */
export function companyDedupeKey(company: { companyName: string; website?: string }): string {
  return normalizeWebsite(company.website) || company.companyName.toLowerCase().trim();
}
